import { Button } from "@mui/material";
import moment from "moment";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { JobPricingReportConstants } from "../../../constants/JobPricingReportConstants";
import {
  JobPricingCustomGrade,
  JobPricingPercentileData,
  JobPricingPercentileDetails,
  JobPricingReportDetails,
  JobPricingReportViewBy
} from "../../../helpers/models/JobPricingDetailModels";
import { JobPricingContext } from "../../../store/JobPricingContext";
import { RootState } from "../../../store/reducers";
import { downloadExcelExportReport } from "./Controller/ExportExcelReport";
import { getPDFExportReport } from "./Controller/ExportPDFReport";

const referenceLevelsList: JobPricingCustomGrade[] = [
  { name: "1", value: "1", low: 34, middle: 36, high: 39 },
  { name: "2", value: "2", low: 40, middle: 43, high: 46 },
  { name: "3", value: "3", low: 47, middle: 50, high: 53 },
  { name: "4", value: "4", low: 54, middle: 58, high: 62 },
  { name: "5", value: "5", low: 63, middle: 67, high: 72 },
  { name: "6", value: "6", low: 73, middle: 78, high: 84 },
  { name: "7", value: "7", low: 85, middle: 91, high: 97 },
  { name: "8", value: "8", low: 98, middle: 105, high: 113 },
  { name: "9", value: "9", low: 114, middle: 124, high: 134 },
  { name: "10", value: "10", low: 135, middle: 147, high: 160 },
  { name: "11", value: "11", low: 161, middle: 176, high: 191 },
  { name: "12", value: "12", low: 192, middle: 209, high: 227 },
  { name: "13", value: "13", low: 228, middle: 248, high: 268 },
  { name: "14", value: "14", low: 269, middle: 291, high: 313 },
  { name: "15", value: "15", low: 314, middle: 342, high: 370 },
  { name: "16", value: "16", low: 371, middle: 404, high: 438 },
  { name: "17", value: "17", low: 439, middle: 478, high: 518 },
  { name: "18", value: "18", low: 519, middle: 566, high: 613 },
  { name: "19", value: "19", low: 614, middle: 674, high: 734 },
  { name: "20", value: "20", low: 735, middle: 807, high: 879 },
  { name: "21", value: "21", low: 880, middle: 967, high: 1055 },
  { name: "22", value: "22", low: 1056, middle: 1158, high: 1260 },
  { name: "23", value: "23", low: 1261, middle: 1384, high: 1507 },
  { name: "24", value: "24", low: 1508, middle: 1654, high: 1800 },
  { name: "25", value: "25", low: 1801, middle: 1970, high: 2140 },
  { name: "26", value: "26", low: 2141, middle: 2345, high: 2550 },
  { name: "27", value: "27", low: 2551, middle: 2785, high: 3020 },
  { name: "28", value: "28", low: 3021, middle: 3300, high: 3580 },
  { name: "29", value: "29", low: 3581, middle: 3915, high: 4250 },
  { name: "30", value: "30", low: 4251, middle: 4655, high: 5060 },
  { name: "31", value: "31", low: 5061, middle: 5540, high: 6020 },
  { name: "32", value: "32", low: 6021, middle: 6590, high: 7160 },
  { name: "33", value: "33", low: 7161, middle: 7740, high: 8320 },
  { name: "34", value: "34", low: 8321, middle: 8980, high: 9640 },
  { name: "35", value: "35", low: 9641, middle: 10410, high: 11180 },
  { name: "36", value: "36", low: 11181, middle: 12080, high: 12980 },
  { name: "37", value: "37", low: 12981, middle: 14030, high: 15080 },
  { name: "38", value: "38", low: 15081, middle: 16310, high: 17540 }
];

export const ExportReport = () => {
  const { t } = useTranslation();
  const { lineChartRef, barChartRef, insightsContent } =
    useContext(JobPricingContext);
  const {
    reportViewBy,
    headerDetails,
    reportDetails,
    targetData,
    publicationMarketDate,
    submissionOrgDate,
    showTableCompElements,
    showSelectedPercentileList,
    clientName,
    isReportDetailsLoading,
    successProfileDetails,
    isSuccessProfileDetailsLoading
  } = useSelector((state: RootState) => state.jobPricingDetails);
  const { selectedCountry } = useSelector((state: RootState) => state.country);
  const { selectedCurrency } = useSelector(
    (state: RootState) => state.currency
  );
  const {
    filtersList,
    filters,
    isFiltersloading,
    peerGroupId,
    selectedPeerGroup: storedSelectedPeerGroup
  } = useSelector((state: RootState) => state.jobPricingList);
  const { peerGroups } = useSelector((state: RootState) => state.peerGroups);
  const getExportReportDateTime = () => {
    return moment().format("MMM DD, YYYY");
  };

  const getExportReportName = (fileExtensionType: string) => {
    return (
      JobPricingReportConstants.JOB_PRICING_EXPORT_NAME +
      " - " +
      getExportReportDateTime() +
      fileExtensionType
    );
  };

  const getCoverData = () => {
    return {
      cover: t("pa.cover"),
      organizationalPerformance: t(
        "pa.organizationalPerformance"
      ).toUpperCase(),
      rewardBenchmarking: t("pa.rewardBenchmarking").toUpperCase(),
      marketData: t("pa.marketData").toUpperCase(),
      singleJobPricing: t("pa.singleJobPricing"),
      company: t("pa.company").toUpperCase(),
      companyName: clientName,
      country: t("pa.country").toUpperCase(),
      selectedCountry: selectedCountry?.label || "",
      selectedCurrency: selectedCurrency ? ` (${selectedCurrency})` : "",
      date: t("pa.date").toUpperCase(),
      copyRight: t("pa.copyright", { year: moment().format("YYYY") })
    };
  };

  const getPeerGroupDetails = () => {
    let peerGroupName = t("pa.noPeerGroupSelected");
    const effectivePeerGroupId =
      peerGroupId ??
      (storedSelectedPeerGroup?.key
        ? Number(storedSelectedPeerGroup.key)
        : undefined);

    if (effectivePeerGroupId && peerGroups?.data?.data?.peerGroups) {
      const selectedPeerGroup = peerGroups.data.data.peerGroups.find(
        (pg: any) => pg.peergroupid == effectivePeerGroupId
      );
      if (selectedPeerGroup) {
        peerGroupName = selectedPeerGroup?.peergroupname;
      }
    }
    return [
      {
        label: t("pa.peergrouptitle"),
        values: [peerGroupName]
      }
    ];
  };

  const getCriteriaFilterDetails = () => {
    const filterData: any[] = [];

    if (filtersList) {
      const getFilteredItems = (
        allItems: any[],
        selectedKeys: (string | number)[] | undefined
      ) => {
        if (selectedKeys && selectedKeys.length > 0) {
          return allItems.filter((item: any) =>
            selectedKeys.includes(item.key)
          );
        }
        return allItems;
      };

      if (
        reportViewBy !== JobPricingReportViewBy.JOB &&
        filtersList?.jobfamily &&
        filtersList?.jobfamily.length > 0
      ) {
        const values: any[] = [];
        const selectedJobFamilyKeys = filters?.jobfamily as
          | (string | number)[]
          | undefined;
        const selectedJobSubFamilyKeys = filters?.jobsubfamily as
          | (string | number)[]
          | undefined;

        const filteredJobFamilies =
          selectedJobFamilyKeys && selectedJobFamilyKeys.length > 0
            ? filtersList.jobfamily.filter((data: any) =>
              selectedJobFamilyKeys.includes(data.key)
            )
            : filtersList.jobfamily;

        filteredJobFamilies.forEach((data: any) => {
          values.push(data.label);
          if (data?.jobsubfamily && data.jobsubfamily.length > 0) {
            const filteredSubFamilies =
              selectedJobSubFamilyKeys && selectedJobSubFamilyKeys.length > 0
                ? data.jobsubfamily.filter((element: any) =>
                  selectedJobSubFamilyKeys.includes(element.key)
                )
                : data.jobsubfamily;

            filteredSubFamilies.forEach((element: any) =>
              values.push(`     ${element.label}`)
            );
          }
        });

        if (values.length > 0) {
          filterData.push({
            label: t("pa.functionTitle"),
            values
          });
        }
      }

      if (filtersList?.industry && filtersList?.industry.length > 0) {
        const filteredIndustries = getFilteredItems(
          filtersList.industry,
          filters?.industry
        );
        if (filteredIndustries.length > 0) {
          filterData.push({
            label: t("pa.industryTitle"),
            values: filteredIndustries.map((data: any) => data.label)
          });
        }
      }
      if (filtersList?.incumbent && filtersList?.incumbent.length > 0) {
        const filteredIncumbents = getFilteredItems(
          filtersList.incumbent,
          filters?.incumbent
        );
        if (filteredIncumbents.length > 0) {
          filterData.push({
            label: t("pa.incumbentsClass"),
            values: filteredIncumbents.map((data: any) => data.label)
          });
        }
      }
      if (filtersList?.region && filtersList?.region.length > 0) {
        const filteredRegions = getFilteredItems(
          filtersList.region,
          filters?.region
        );
        if (filteredRegions.length > 0) {
          filterData.push({
            label: t("pa.regionTitle"),
            values: filteredRegions.map((data: any) => data.label)
          });
        }
      }
      if (
        filtersList?.clientownership &&
        filtersList?.clientownership.length > 0
      ) {
        const filteredOwnership = getFilteredItems(
          filtersList.clientownership,
          filters?.clientownership
        );
        if (filteredOwnership.length > 0) {
          filterData.push({
            label: t("pa.ownership"),
            values: filteredOwnership.map((data: any) => data.label)
          });
        }
      }
      if (filtersList?.numofemp && filtersList?.numofemp.length > 0) {
        const filteredNumOfEmp = getFilteredItems(
          filtersList.numofemp,
          filters?.numofemp
        );
        if (filteredNumOfEmp.length > 0) {
          filterData.push({
            label: t("pa.employeeCountTitle"),
            values: filteredNumOfEmp.map((data: any) => data.label)
          });
        }
      }
      if (
        filtersList?.revenuerangedesc &&
        filtersList?.revenuerangedesc.length > 0
      ) {
        const filteredRevenueRange = getFilteredItems(
          filtersList.revenuerangedesc,
          filters?.revenuerangedesc
        );
        if (filteredRevenueRange.length > 0) {
          filterData.push({
            label: t("pa.revenueRangeTitle"),
            values: filteredRevenueRange.map((data: any) => data.label)
          });
        }
      }
      if (
        reportViewBy === JobPricingReportViewBy.FUNCTION &&
        filtersList?.grade &&
        filtersList?.grade.length > 0
      ) {
        const filteredGrades = getFilteredItems(
          filtersList.grade,
          filters?.grade
        );
        if (filteredGrades.length > 0) {
          filterData.push({
            label: t("pa.grade"),
            values: filteredGrades.map((data: any) => data.label)
          });
        }
      }
    }
    return filterData;
  };

  const getCriteriaHeaderDetails = () => {
    if (reportViewBy === JobPricingReportViewBy.JOB) {
      return [
        { label: t("pa.jobTitle"), values: [headerDetails.title] },
        { label: t("pa.jobCode"), values: [headerDetails.jobCode] },
        { label: t("pa.grade"), values: [headerDetails.grade] },
        { label: t("pa.jobFunction"), values: [headerDetails.function] },
        { label: t("pa.jobSubFunction"), values: [headerDetails.subFunction] }
      ];
    } else if (reportViewBy === JobPricingReportViewBy.FUNCTION) {
      return [
        { label: t("pa.functionTitle"), values: [headerDetails.title] },
        { label: t("pa.totalJobs"), values: [headerDetails.totalJobs] },
        { label: t("pa.gradeSpan"), values: [headerDetails.gradeSpan] }
      ];
    } else if (reportViewBy === JobPricingReportViewBy.GRADE) {
      return [
        { label: t("pa.gradeTitle"), values: [headerDetails.title] },
        { label: t("pa.totalJobs"), values: [headerDetails.totalJobs] },
        { label: t("pa.jobsInOrg"), values: [headerDetails.orgJobs] }
      ];
    } else {
      return [];
    }
  };

  const getCriteriaData = () => {
    return {
      analysisCriteria: t("pa.analysisCriteria"),
      marketCriteria: t("pa.marketCriteria"),
      dataCriteria: [
        ...getCriteriaHeaderDetails(),
        {
          label: t("pa.targetPercentile"),
          values: [targetData.percentileDisplay]
        },
        {
          label: t("pa.percentileTitle"),
          values: showSelectedPercentileList.map(
            (data: JobPricingPercentileData) => data.percentileDisplay
          )
        },
        {
          label: t("pa.compensationElement"),
          values: showTableCompElements
        },
        {
          label: t("pa.publicationMarket"),
          values: [publicationMarketDate]
        },
        {
          label: t("pa.submissionOrg"),
          values: [submissionOrgDate]
        },
        ...getPeerGroupDetails(),
        ...getCriteriaFilterDetails()
      ]
    };
  };

  const getTableHeaderList = () => {
    const tableHeaderList = [
      t("pa.element"),
      t("pa.target") + ": " + targetData?.percentileDisplay || "",
      t("pa.myOrgavg"),
      t("pa.myOrg") + " " + targetData?.percentileDisplay || ""
    ];
    showSelectedPercentileList.map((data: JobPricingPercentileData) =>
      tableHeaderList.push(data.percentileDisplay)
    );
    tableHeaderList.push(t("pa.sjpr.incumbents"));
    tableHeaderList.push(t("pa.sjpr.organizations"));
    return tableHeaderList;
  };

  const getTableDetailsList = () => {
    const tableDetailsList: any[] = [];
    const percentileHeaderValues =
      showSelectedPercentileList.map(
        (data: JobPricingPercentileData) => data.percentile
      ) || [];
    reportDetails
      .filter((data: JobPricingReportDetails, index: number) =>
        showTableCompElements.includes(data.factName)
      )
      .forEach((data: JobPricingReportDetails, index: number) => {
        const rowData = [];
        rowData.push(data.factName);
        rowData.push(
          data.targetMarket ? data.targetMarket.toLocaleString() : "-"
        );
        const avgOrgPercentage =
          data.avgOrgPercentage && data.avgOrgPercentage > 0
            ? " (" + data.avgOrgPercentage + "%)"
            : "";
        rowData.push(
          data.avgOrg ? data.avgOrg.toLocaleString() + avgOrgPercentage : "-"
        );
        rowData.push(data.targetOrg ? data.targetOrg.toLocaleString() : "-");
        data.percentileDetails
          ?.filter(
            (percentileData: JobPricingPercentileDetails, index: number) =>
              percentileHeaderValues.includes(percentileData.percentileValue)
          )
          .map((percentileData: JobPricingPercentileDetails, index: number) =>
            rowData.push(
              percentileData.marketValue
                ? percentileData.marketValue.toLocaleString()
                : "-"
            )
          );
        rowData.push(data.incumbents || "-");
        rowData.push(data.orgs || "-");
        tableDetailsList.push(rowData);
      });
    return tableDetailsList;
  };

  const getTableData = () => {
    return {
      jobPricing: t("pa.jobPricing"),
      tableHeaderList: getTableHeaderList(),
      tableDetailsList: getTableDetailsList()
    };
  };

  const getDefinitionsData = () => {
    const definitionTextList = [];
    for (let index = 1; index <= 26; index++) {
      definitionTextList.push({
        defKey: t("pa.defKey" + index),
        defValue: t("pa.defValue" + index)
      });
    }
    return {
      definitions: t("pa.definitions"),
      generalDefinitions: t("pa.generalDefinitions"),
      definitionTextList,
      referenceLevelsList
    };
  };

  const exportExcelDocument = () => {
    const excelDocDetails = {
      coverData: getCoverData(),
      criteriaData: getCriteriaData(),
      tableData: getTableData(),
      definitionsData: getDefinitionsData()
    };
    downloadExcelExportReport(excelDocDetails, getExportReportName(".xlsx"));
  };

  const getDocHeaderDetails = () => {
    if (reportViewBy === JobPricingReportViewBy.JOB) {
      return [
        { label: t("pa.jobTitle"), value: headerDetails.title },
        { label: t("pa.headerJobcode"), value: headerDetails.jobCode },
        { label: t("pa.grade"), value: headerDetails.grade },
        { label: "", value: headerDetails.function },
        { label: "", value: headerDetails.subFunction }
      ];
    } else if (reportViewBy === JobPricingReportViewBy.FUNCTION) {
      return [
        { label: t("pa.functionTitle"), value: headerDetails.title },
        { label: t("pa.headerTotalJobs"), value: headerDetails.totalJobs },
        { label: t("pa.gradeSpan"), value: headerDetails.gradeSpan }
      ];
    } else if (reportViewBy === JobPricingReportViewBy.GRADE) {
      return [
        { label: t("pa.gradeTitle"), value: headerDetails.title },
        { label: t("pa.headerTotalJobs"), value: headerDetails.totalJobs },
        { label: t("pa.headerJobsInOrg"), value: headerDetails.orgJobs }
      ];
    } else {
      return [];
    }
  };

  const getDocDescriptionDetails = () => {
    if (
      reportViewBy === JobPricingReportViewBy.JOB &&
      headerDetails.description
    ) {
      return {
        descriptionTitle: t("pa.description"),
        descriptionText: headerDetails.description
      };
    } else {
      return null;
    }
  };

  const getDocInsightsDetails = () => {
    return {
      insightsTitle: t("pa.insights"),
      yourOrganizations: t("pa.yourOrganizations"),
      insightsDataDescriptionP1: t("pa.insightsDataAvgOrgDescriptionP1"),
      insightsDataDescriptionP2: t("pa.insightsDataAvgOrgDescriptionP2"),
      insightsNoDataDescriptionP1: t("pa.insightsDataNonAvgOrgDescriptionP1"),
      insightsNoDataDescriptionP2: t("pa.insightsDataNonAvgOrgDescriptionP2"),
      insightsMedianAnnualSalary: t("pa.medianAnnualSalary"),
      insightsPriorYearDescription: t("pa.reportsInsightsDescription"),
      insightsTargetData: targetData.percentileDisplay,
      insightsContent: insightsContent
    };
  };

  const getDocTargetDetails = () => {
    return {
      label: t("pa.target"),
      value: targetData.percentileDisplay
    };
  };

  const getDocChartDetails = () => {
    const lineChartRefSVG =
      lineChartRef?.getSVG({
        ...lineChartRef.options.exporting?.chartOptions
      }) || "";
    const barChartRefSVG =
      barChartRef?.getSVG({
        ...barChartRef.options.exporting?.chartOptions
      }) || "";
    return {
      lineChartRefSVG,
      barChartRefSVG,
      fixedVariableNote: t("pa.fixedVariableNote")
    };
  };

  const getDocTableDetails = () => {
    return {
      tableHeaderList: getTableHeaderList(),
      tableDetailsList: getTableDetailsList()
    };
  };

  const getDocFooterDetails = () => {
    return {
      publicationMarketLabel: t("pa.publicationMarket"),
      publicationMarketValue: publicationMarketDate,
      submissionOrgLabel: t("pa.submissionOrg"),
      submissionOrgValue: submissionOrgDate
    };
  };

  const getSuccessProfileDetails = () => {
    return {
      profileTitle: headerDetails.title,
      jobSummaryTitle: t("pa.jobSummary"),
      skillsTitle: t("pa.skills"),
      behavioralCompetenciesTitle: t("pa.behavioralCompetencies"),
      successProfileDetails
    };
  };

  const exportPDFDocument = () => {
    const pdfDocDetails = {
      headerDetails: getDocHeaderDetails(),
      descriptionDetails: getDocDescriptionDetails(),
      insightsDetails: getDocInsightsDetails(),
      targetDetails: getDocTargetDetails(),
      chartDetails: getDocChartDetails(),
      tableDetails: getDocTableDetails(),
      footerDetails: getDocFooterDetails(),
      successProfileDetails: getSuccessProfileDetails()
    };
    getPDFExportReport(pdfDocDetails, getExportReportName(".pdf"));
  };

  const exportReportDocuments = () => {
    exportPDFDocument();
    exportExcelDocument();
  };
  return (
    <Button
      variant="contained"
      sx={{
        textTransform: "none",
        backgroundColor: "var(--primary-color)",
        color: "var(--contained-button-text)",
        borderColor: "var(--button-text)",
        fontWeight: 800,
        "&:hover": {
          backgroundColor: "var(--primary-color)"
        }
      }}
      onClick={exportReportDocuments}
      disabled={
        isReportDetailsLoading ||
        isFiltersloading ||
        !(reportDetails && reportDetails.length > 0)
      }
    >
      {t("pa.export")}
    </Button>
  );
};
